import {
  IsString,
  IsNumber,
  IsIn,
  IsBoolean,
  IsOptional,
  IsDefined,
  ValidateNested,
  Min,
  Max,
} from 'class-validator';
import { Type } from 'class-transformer';
import { IsColorOrReference } from '../../styles/validators/IsColorOrReference';
import { IsRgbaOrReference } from '../../styles/validators/IsRgbaOrReference';
import {
  VisibilityStateDto,
  PositionAnchorsStateDto,
  InteractionEventDto,
  ShadowSettingsDto,
  SHADOW_TYPE_VALUES,
  type ShadowType,
} from './shared';
import { IsTRBLNumberCoords } from '../validators/IsTRBLNumberCoords';
import {
  BUTTON_SIZES,
  BUTTON_VARIANTS,
  BUTTON_TYPES,
  type ButtonSize,
  type ButtonVariant,
  type ButtonType,
} from '@builder4/shared/styles/constants';
import {
  ANIMATION_TYPES,
  ANIMATION_EASINGS,
  HOVER_EFFECT_TYPES,
} from '@builder4/shared/types/animation';
import { GradientDto, RgbaDto } from '../../styles';

const TEXT_ALIGN_VALUES = ['left', 'right', 'center', 'justify', 'start', 'end'] as const;
const POSITION_VALUES = ['relative', 'absolute', 'fixed', 'sticky'] as const;

export class ButtonBreakpointPropsDto {
  @IsOptional()
  @IsIn([...BUTTON_TYPES])
  buttonType?: ButtonType;

  @IsOptional()
  @IsTRBLNumberCoords()
  padding?: [number, number, number, number];

  @IsOptional()
  @IsTRBLNumberCoords()
  margin?: [number, number, number, number];

  @IsOptional()
  @IsTRBLNumberCoords()
  borderRadius?: [number, number, number, number];

  @IsOptional()
  @IsIn([...POSITION_VALUES])
  position?: 'relative' | 'absolute' | 'fixed' | 'sticky';

  @IsOptional()
  @IsString()
  top?: string;

  @IsOptional()
  @IsString()
  left?: string;

  @IsOptional()
  @IsString()
  right?: string;

  @IsOptional()
  @IsString()
  bottom?: string;

  @IsOptional()
  @IsNumber()
  @Min(-1)
  @Max(100)
  zIndex?: number;

  @IsOptional()
  @ValidateNested()
  @Type(() => PositionAnchorsStateDto)
  positionAnchorsState?: PositionAnchorsStateDto;
}

export class ButtonPropsDto {
  @IsDefined()
  @IsIn([...BUTTON_SIZES])
  buttonSize!: ButtonSize;

  @IsDefined()
  @IsIn([...BUTTON_VARIANTS])
  buttonVariant!: ButtonVariant;

  @IsDefined()
  @IsString()
  minWidth!: string;

  @IsDefined()
  @IsString()
  minHeight!: string;

  @IsDefined()
  @IsString()
  width!: string;

  @IsDefined()
  @IsString()
  height!: string;

  @IsOptional()
  @IsIn([...BUTTON_TYPES])
  buttonType?: ButtonType;

  @IsDefined()
  @ValidateNested()
  @Type(() => VisibilityStateDto)
  visibility!: VisibilityStateDto;

  @IsDefined()
  @IsTRBLNumberCoords()
  padding!: [number, number, number, number];

  @IsOptional()
  @IsTRBLNumberCoords()
  margin?: [number, number, number, number];

  @IsDefined()
  @IsTRBLNumberCoords()
  borderRadius!: [number, number, number, number];

  @IsOptional()
  @IsIn([...POSITION_VALUES])
  position?: 'relative' | 'absolute' | 'fixed' | 'sticky';

  @IsOptional()
  @IsString()
  top?: string;

  @IsOptional()
  @IsString()
  left?: string;

  @IsOptional()
  @IsString()
  right?: string;

  @IsOptional()
  @IsString()
  bottom?: string;

  @IsOptional()
  @IsRgbaOrReference()
  borderColor?: RgbaDto | string;

  @IsOptional()
  @IsColorOrReference()
  color?: RgbaDto | GradientDto | string;

  @IsOptional()
  @ValidateNested()
  @Type(() => RgbaDto)
  textSolid?: RgbaDto;

  @IsOptional()
  @ValidateNested()
  @Type(() => GradientDto)
  textGradient?: GradientDto;

  @IsOptional()
  @IsColorOrReference()
  backgroundColor?: RgbaDto | GradientDto | string;

  @IsOptional()
  @ValidateNested()
  @Type(() => GradientDto)
  bgGradient?: GradientDto;

  @IsOptional()
  @ValidateNested()
  @Type(() => RgbaDto)
  bgSolid?: RgbaDto;

  @IsDefined()
  @IsNumber()
  @Min(0)
  borderWidth!: number;

  @IsDefined()
  @IsIn([...TEXT_ALIGN_VALUES])
  textAlign!: 'left' | 'right' | 'center' | 'justify' | 'start' | 'end';

  @IsDefined()
  @IsString()
  fontSize!: string;

  @IsOptional()
  @IsString()
  fontWeight?: string;

  @IsDefined()
  @IsString()
  fontFamily!: string;

  @IsOptional()
  @IsString()
  fontStyle?: string;

  @IsDefined()
  @IsString()
  text!: string;

  @IsDefined()
  @IsNumber()
  letterSpacing!: number;

  @IsDefined()
  @IsBoolean()
  appliedGlobalStyle!: boolean;

  @IsDefined()
  @IsNumber()
  zIndex!: number;

  @IsDefined()
  @ValidateNested()
  @Type(() => PositionAnchorsStateDto)
  positionAnchorsState!: PositionAnchorsStateDto;

  @IsDefined()
  @ValidateNested()
  @Type(() => InteractionEventDto)
  clickEvent!: InteractionEventDto;

  @IsOptional()
  @IsRgbaOrReference()
  hoverBorderColor?: RgbaDto | string;

  @IsOptional()
  @IsColorOrReference()
  hoverBackgroundColor?: RgbaDto | GradientDto | string;

  @IsOptional()
  @ValidateNested()
  @Type(() => GradientDto)
  hoverBackgroundGradient?: GradientDto;

  @IsOptional()
  @ValidateNested()
  @Type(() => RgbaDto)
  hoverBackgroundSolid?: RgbaDto;

  @IsOptional()
  @IsColorOrReference()
  hoverTextColor?: RgbaDto | GradientDto | string;

  @IsOptional()
  @ValidateNested()
  @Type(() => RgbaDto)
  hoverTextSolid?: RgbaDto;

  @IsOptional()
  @ValidateNested()
  @Type(() => GradientDto)
  hoverTextGradient?: GradientDto;

  @IsOptional()
  @IsBoolean()
  fillWidth?: boolean;

  @IsOptional()
  @IsString()
  fillSpace?: 'yes' | 'no';

  @IsOptional()
  @IsString()
  flexBasis?: string;

  // Animation
  @IsOptional()
  @IsIn([...ANIMATION_TYPES])
  animationType?: (typeof ANIMATION_TYPES)[number];

  @IsOptional()
  @IsNumber()
  @Min(100)
  @Max(2000)
  animationDuration?: number;

  @IsOptional()
  @IsNumber()
  @Min(0)
  @Max(2000)
  animationDelay?: number;

  @IsOptional()
  @IsIn([...ANIMATION_EASINGS])
  animationEasing?: (typeof ANIMATION_EASINGS)[number];

  // Hover effect
  @IsOptional()
  @IsIn([...HOVER_EFFECT_TYPES])
  hoverEffect?: (typeof HOVER_EFFECT_TYPES)[number];

  @IsOptional()
  @IsNumber()
  @Min(0)
  @Max(2000)
  hoverTransitionDuration?: number;

  @IsOptional()
  @IsNumber()
  @Min(0)
  @Max(200)
  hoverIntensity?: number;
  @IsOptional()
  @IsIn([...SHADOW_TYPE_VALUES])
  shadowType?: ShadowType;

  @IsOptional()
  @ValidateNested()
  @Type(() => ShadowSettingsDto)
  shadowSettings?: ShadowSettingsDto;

  // Breakpoint overrides (optional, partial props)
  @IsOptional()
  @ValidateNested()
  @Type(() => ButtonBreakpointPropsDto)
  tablet?: ButtonBreakpointPropsDto;

  @IsOptional()
  @ValidateNested()
  @Type(() => ButtonBreakpointPropsDto)
  mobile?: ButtonBreakpointPropsDto;
}
